// Lightweight inline chat renderer for playbook agent steps using ai-elements
// primitives directly. For the DB-backed full chat widget (NoxusMessageContent),
// see ChatViewerDetail.tsx which is used by BasicAgentNodeV3's detail view.
import React from "react";
import {
  Message,
  MessageContent,
  MessageResponse,
} from "@/components/ai-elements/message";
import {
  Tool,
  ToolHeader,
  ToolContent,
  ToolInput,
  ToolOutput,
} from "@/components/ai-elements/tool";
import { cn } from "@/lib/utils";
import { Bot, User } from "lucide-react";

interface TextPart {
  type: "text";
  text: string;
}

interface ToolCallPart {
  type: "tool-call";
  toolName: string;
  args: Record<string, unknown>;
}

interface ToolResultPart {
  type: "tool-result";
  toolName: string;
  output: string;
}

type ChatPart = TextPart | ToolCallPart | ToolResultPart;

export interface ChatMessage {
  role: "user" | "assistant";
  parts: ChatPart[];
}

interface AgentChatViewProps {
  messages: ChatMessage[];
  className?: string;
  maxHeight?: number;
}

function ChatPartRenderer({ part }: { part: ChatPart }) {
  switch (part.type) {
    case "text":
      return <MessageResponse>{part.text}</MessageResponse>;

    case "tool-call":
      return (
        <Tool defaultOpen={false}>
          <ToolHeader
            title={part.toolName}
            type="tool-invocation"
            state="output-available"
          />
          <ToolContent>
            <ToolInput input={part.args} />
          </ToolContent>
        </Tool>
      );

    case "tool-result":
      return (
        <Tool defaultOpen={false}>
          <ToolHeader
            title={`${part.toolName} result`}
            type="tool-invocation"
            state="output-available"
          />
          <ToolContent>
            <ToolOutput output={part.output} errorText={undefined} />
          </ToolContent>
        </Tool>
      );

    default:
      return null;
  }
}

function ChatMessageRow({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  // Skip messages that only have tool-result parts (internal plumbing)
  const hasVisibleContent = message.parts.some(
    (p) => p.type === "text" || p.type === "tool-call"
  );

  if (!hasVisibleContent && isUser) {
    return null;
  }

  return (
    <Message from={message.role} compact>
      <div className="flex items-start gap-2">
        <div
          className={cn(
            "flex-shrink-0 rounded-full p-1.5",
            isUser ? "bg-accent" : "bg-primary/10"
          )}
        >
          {isUser ? (
            <User className="size-3.5 text-muted-foreground" />
          ) : (
            <Bot className="size-3.5 text-primary" />
          )}
        </div>
        <MessageContent className="flex-1 min-w-0">
          {message.parts.map((part, i) => (
            <ChatPartRenderer key={i} part={part} />
          ))}
        </MessageContent>
      </div>
    </Message>
  );
}

export function AgentChatView({
  messages,
  className,
  maxHeight = 500,
}: AgentChatViewProps) {
  if (!messages || messages.length === 0) {
    return null;
  }

  return (
    <div
      className={cn("space-y-4 overflow-y-auto pr-2", className)}
      style={{ maxHeight: `${maxHeight}px` }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Bot className="size-4 text-primary" />
        <span className="text-sm font-medium">Agent Conversation</span>
      </div>
      {messages.map((msg, i) => (
        <ChatMessageRow key={i} message={msg} />
      ))}
    </div>
  );
}
